import type {
  ContractIntelligenceContract,
  ContractIntelligenceRecent,
} from "@/types/contractIntelligence.types";
import { DEMO_CONTRACT_IDS } from "@/mockData/demoContractData";

export const SAMPLE_CONTRACT: ContractIntelligenceContract = {
  id: "midnight-records",
  name: "Recording Services & Master Rights Agreement",
  counterparty: "Midnight Records LLC",
  artist: "Alexandra Rivers",
  type: "Recording Deal",
  typeColor: "#8b5cf6",
  effectiveDate: "Jan 15, 2024",
  pages: 24,
  riskScore: 68,
  status: "Under Review",
  advance: 420,
  royalty: 18,
  backendRoyalty: 3,
  term: "24 months",
  masters: 12,
  analysisContractId: DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL,
  keyTerms: {
    financial: [
      { label: "Total Advance", value: "$420K", sub: "$95K recoupable", flag: "medium" },
      { label: "Royalty Rate", value: "18%", sub: "Of net wholesale", flag: "low" },
      { label: "Backend Royalty", value: "3%", sub: "After full recoupment", flag: "high" },
      { label: "Mechanical Cap", value: "75%", sub: "Of statutory rate", flag: "medium" },
    ],
    schedule: [
      { milestone: "On Execution", amount: "$145K", date: "Jan 15, 2024", status: "paid" },
      { milestone: "First EP Delivery", amount: "$80K", date: "Jul 2024", status: "paid" },
      { milestone: "Second EP Commencement", amount: "$100K", date: "Mar 2025", status: "paid" },
      { milestone: "Production Milestones", amount: "$95K", date: "Q4 2025", status: "pending" },
    ],
    obligations: [
      {
        type: "Deliverable",
        text: "Minimum 12 commercially-satisfactory Masters during Initial Term",
        risk: "high",
      },
      { type: "Exclusivity", text: "Exclusive recording services to Label during Initial Term", risk: "medium" },
      { type: "Approval", text: "Producer approval required before commencement", risk: "low" },
      {
        type: "Marketing",
        text: "Artist commits to 60 days promotional touring per album",
        risk: "medium",
      },
    ],
    rights: [
      { name: "Master Recordings", owner: "Label", term: "In Perpetuity", risk: "high" },
      { name: "Composition Rights", owner: "Artist", term: "Retained", risk: "low" },
      { name: "Sync Licensing", owner: "Label (50/50)", term: "Initial Term + 5yr", risk: "medium" },
      { name: "Merchandising", owner: "Artist", term: "Retained", risk: "low" },
      { name: "Name & Likeness", owner: "Label (limited)", term: "Initial Term", risk: "medium" },
    ],
    dates: [
      { label: "Effective Date", date: "Jan 15, 2024", status: "past" },
      { label: "First EP Due", date: "Jul 1, 2024", status: "past" },
      { label: "Second EP Due", date: "Mar 30, 2025", status: "past" },
      { label: "Initial Term Ends", date: "Jan 14, 2026", status: "upcoming" },
      { label: "Option Exercise By", date: "Nov 14, 2025", status: "critical" },
    ],
    flags: [
      {
        severity: "high",
        term: "Master Ownership in Perpetuity",
        clause: "§5.1",
        note: "Standard catalog reverts after 35 years under US copyright law — not addressed.",
      },
      {
        severity: "high",
        term: "Cross-collateralization",
        clause: "§4.3",
        note: "Recoupment pooled across all albums. Push for per-album recoupment.",
      },
      {
        severity: "medium",
        term: "Producer Approval Window",
        clause: "§2.3",
        note: "14-day silent approval — too short for talent of this profile.",
      },
      {
        severity: "medium",
        term: "360 Touring Carve-out",
        clause: "§7.2",
        note: "Label takes 15% of touring net — unusual for indie deal.",
      },
      {
        severity: "low",
        term: "Audit Rights",
        clause: "§9.4",
        note: "Once-per-year cap is industry standard, but no extended look-back.",
      },
    ],
  },
  contractText: `RECORDING SERVICES AND MASTER RIGHTS AGREEMENT

Effective Date: January 15, 2024

Parties: This Recording Services and Master Rights Agreement ("Agreement") is entered into by and between Midnight Records LLC ("Label"), and Alexandra Rivers ("Artist").

1. DEFINITIONS
1.1 "Initial Term" means the two (2) year period beginning on the Effective Date unless earlier terminated under this Agreement.
1.2 "Masters" means all master recordings created by or for Artist during the Initial Term.

2. ENGAGEMENT AND DELIVERY
2.1 Label engages Artist to render exclusive recording services for the production and delivery of Masters.
2.2 Artist shall deliver no fewer than twelve (12) commercially satisfactory Masters during the Initial Term.
2.3 Artist shall not record or deliver Masters for any third party during the Initial Term without Label's prior written consent.

3. ADVANCE & COMPENSATION
3.1 Label shall pay Artist a Total Advance of $420,000, payable in installments described below.
3.2 Royalties of eighteen percent (18%) of net wholesale shall accrue to Artist post-recoupment.

4. RECOUPMENT AND ACCOUNTING
4.1 Label may recoup recording costs from royalties before paying further advances.
4.2 Recoupment shall be cross-collateralized across all albums delivered during the Initial Term and any exercised Options.

5. OWNERSHIP OF MASTERS
5.1 All Masters created during the Initial Term shall be owned by Label in perpetuity throughout the universe.
5.2 Artist hereby assigns all right, title, and interest in such Masters to Label.

6. BACKEND ROYALTY
6.1 Following full recoupment, Artist shall be entitled to a Backend Royalty of three percent (3%) of Gross Receipts.

7. ADDITIONAL RIGHTS
7.1 Composition rights are retained by Artist.
7.2 Label shall be entitled to fifteen percent (15%) of net touring revenue during the Initial Term.

8. TERM AND OPTIONS
8.1 Label holds three (3) one-year options exercisable in writing no later than sixty (60) days prior to the end of the then-current term.`,
};

export const RECENT_CONTRACTS: ContractIntelligenceRecent[] = [
  { name: "Nike — LeBron Q2 Amendment", date: "Apr 28", type: "Brand", color: "#3b82f6", risk: "low" },
  { name: "Travis Scott × McDonald's Phase 2", date: "Apr 22", type: "Brand", color: "#3b82f6", risk: "medium" },
  { name: "Beyoncé — Netflix Streaming Rider", date: "Apr 18", type: "Licensing", color: "#14b8a6", risk: "high" },
  { name: "Roc Nation × Tidal Equity Ext.", date: "Apr 12", type: "Music", color: "#8b5cf6", risk: "medium" },
  { name: "Serena × Wilson — Legacy Endorsement", date: "Apr 4", type: "Sports", color: "#22c55e", risk: "low" },
];

export const PARSE_STEPS = [
  "Reading document structure",
  "Extracting parties and dates",
  "Identifying financial terms",
  "Mapping ownership and rights",
  "Flagging off-market clauses",
];

export const EDITOR_STARTER_MESSAGE =
  "I've finished reading the Recording Services Agreement. Two clauses are off-market and worth pushing back on: master ownership in perpetuity (§5.1) and the cross-collateralized recoupment (§4.3). Want me to draft redlines, or do you have a specific clause in mind?";

export const SUGGESTED_EDITS = [
  { icon: "⚖️", label: "Push back on §5.1 master ownership", prompt: "Redline §5.1 — propose master rights revert to Artist after 7 years post-Initial Term." },
  { icon: "💰", label: "Renegotiate backend royalty", prompt: "Push the Backend Royalty in §6.1 from 3% to 5% of Gross Receipts." },
  { icon: "🔄", label: "Decouple cross-collateralization", prompt: "Rewrite §4.3 so recoupment is per-album, not cross-collateralized." },
  { icon: "📅", label: "Extend approval windows", prompt: "Extend the producer approval window in §2.3 from 14 to 30 days." },
];

export const RISK_COLORS: Record<string, string> = {
  high: "text-destructive",
  medium: "text-amber-500",
  low: "text-success",
};
